import React from 'react';
import {
    Box,
    darken,
    IconButton,
    lighten,
    Menu,
    MenuItem,
    SxProps,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    TypographyProps,
    useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import { Checkbox } from '@europrocurement/flexy-components';
import { ColumnHeaderItemSortabled, SortDirection } from '../ColumnHeaderItemSortabled';
import { ColumnDatatable } from '../ColumnDatatable';
import { SearchOpts } from '../DatatableHeader';
import useSelectItem from '../FlexyDatatableTable/useSelectItem';
import {
    FlexyDatatableSelectionAction,
    RowSelectionAction,
} from '../FlexyDatatableSelectionAction';

export type FlexyDatatableHeaderProps<T = Record<string, unknown>> = {
    columns: Array<ColumnDatatable<T>>;
    searchOpts?: SearchOpts;
    columnLabelVariant?: TypographyProps['variant'];
    columnLabelColor?: TypographyProps['color'];
    itemsPerPage?: number;
    hideColumnOptions?: boolean;
    setColumns?: React.Dispatch<React.SetStateAction<ColumnDatatable<T>[]>>;
    rowsActions?: Array<RowSelectionAction>;
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
};

export const FlexyDatatableHeader = function <T extends Record<string, unknown>>(
    props: FlexyDatatableHeaderProps<T>,
) {
    const {
        columns,
        searchOpts,
        columnLabelVariant,
        itemsPerPage,
        hideColumnOptions = false,
        setColumns = null,
        rowsActions = [],
        onClick = () => {},
    } = props;
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const theme = useTheme();
    const { palette } = theme;
    const isLightMode = palette.mode === 'light';

    const headerBgColor = isLightMode ? palette.grey[50] : palette.grey[900];
    const headerBorderColor = isLightMode ? palette.grey[300] : palette.grey[700];
    const columnLabelColor = isLightMode
        ? lighten(palette.getContrastText(headerBgColor), 0.3)
        : darken(palette.getContrastText(headerBgColor), 0.1);
    const openDisplayColumn = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const countDisplayed = columns.filter((column) => column.isDisplayed).length;

    const { countSelectedItems, hasSelectedItems, handleSelectAllItems, countUnselectedItems } =
        useSelectItem();
    const selectAllCkb = (
        <Checkbox
            checked={countUnselectedItems() === 0}
            indeterminate={
                itemsPerPage !== undefined &&
                countUnselectedItems() > 0 &&
                countUnselectedItems() < itemsPerPage
            }
            onChange={handleSelectAllItems}
            size="small"
        />
    );

    const celStyle: SxProps = {
        padding: '7px 8px',
        textAlign: 'left',
        verticalAlign: 'middle',
        justifyContent: 'flex-start',
        backgroundColor: headerBgColor,
        borderTopColor: headerBorderColor,
        borderBottomColor: 'transparent',
        maxHeight: '55px',
    };

    const innerColumns = columns.filter((column) => column.isDisplayed !== false);

    if (hasSelectedItems() && rowsActions.length > 0) {
        const highlightTextColor = palette.getContrastText(palette.primary.main);
        const highlightStyle = {
            ...celStyle,
            backgroundColor: palette.primary.main,
            color: highlightTextColor,
            fontSize: '14px',
            lineHeight: '25.2px',
            width: 'auto',
        };

        return (
            <TableHead>
                <TableRow>
                    <TableCell
                        colSpan={countDisplayed + 1}
                        sx={{
                            ...highlightStyle,
                            borderTopLeftRadius: '12px',
                            borderTopRightRadius: '0',
                            zIndex: 'auto',
                        }}
                    >
                        <Box sx={{ display: 'flex' }}>
                            {selectAllCkb}
                            <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                <Box>{countSelectedItems()} sélection(s)</Box>
                                {rowsActions.map((rowAction) => (
                                    <FlexyDatatableSelectionAction
                                        key={rowAction.label}
                                        {...rowAction}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </TableCell>
                </TableRow>
            </TableHead>
        );
    }

    return (
        <TableHead data-testid="FlexyDatatableHead">
            <TableRow>
                {rowsActions.length > 0 && (
                    <TableCell
                        key="select-item-cell"
                        sx={{
                            ...celStyle,
                            borderTopLeftRadius: '12px',
                            borderTopRightRadius: '0',
                            width: '60px',
                            display: {
                                xs: 'none',
                                sm: 'none',
                                md: 'table-cell',
                                lg: 'table-cell',
                                xl: 'table-cell',
                            },
                            zIndex: 'auto',
                        }}
                    >
                        {selectAllCkb}
                    </TableCell>
                )}
                {innerColumns?.map((column, i) => {
                    // Determine if column is sortable and get current sort state
                    let orderable = false;
                    let defaultOrder: SortDirection;

                    if (column.sortable && searchOpts?.ordersOpts?.orders) {
                        orderable = true;
                        if (column.field && searchOpts.ordersOpts?.orders) {
                            const currentOrder = searchOpts.ordersOpts.orders.find(
                                (item) => item.field === column.field,
                            );
                            if (currentOrder) {
                                defaultOrder = currentOrder.value as SortDirection;
                            }
                        }
                    }

                    return (
                        <TableCell
                            key={column.label}
                            sx={{
                                ...celStyle,
                                cursor: 'pointer',
                                borderTopRightRadius:
                                    i + 1 === innerColumns.length ? '12px' : '0px',
                                borderTopLeftRadius:
                                    i === 0 && rowsActions.length <= 0 ? '12px' : '0px',
                                paddingLeft: i === 0 ? '15px' : undefined,
                                ...column.sx,
                                zIndex: 'auto',
                            }}
                            onClick={onClick}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    minHeight: '40px',
                                    width: '100%',
                                }}
                            >
                                {orderable && searchOpts?.ordersOpts ? (
                                    <Tooltip
                                        placement="top"
                                        arrow
                                        title={column.headerTooltip || ''}
                                    >
                                        <ColumnHeaderItemSortabled
                                            onOrdersChange={searchOpts.ordersOpts?.onOrdersChange}
                                            defaultValue={defaultOrder}
                                            field={column.field}
                                            label={
                                                !(column.displayLabel === false) ? column.label : ''
                                            }
                                            labelVariant={columnLabelVariant}
                                            defaultLabelColor={columnLabelColor}
                                        />
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        placement="top"
                                        arrow
                                        title={column.headerTooltip || ''}
                                    >
                                        <Typography
                                            variant={columnLabelVariant}
                                            color={columnLabelColor}
                                        >
                                            {!(column.displayLabel === false) && column.label}
                                        </Typography>
                                    </Tooltip>
                                )}
                            </Box>
                        </TableCell>
                    );
                })}
                {!hideColumnOptions && (
                    <TableCell
                        key="header-actions-cell"
                        sx={{
                            ...celStyle,
                            borderTopLeftRadius:
                                innerColumns.length === 0 && rowsActions.length <= 0
                                    ? '12px'
                                    : '0px',
                            borderTopRightRadius: '12px',
                            width: '48px',
                            zIndex: 'auto',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip
                                title="affichage colonnes"
                                data-testid="DisplayFiltersButton"
                            >
                                <IconButton onClick={openDisplayColumn}>
                                    <MoreVertIcon sx={{ color: 'inherit' }} />
                                </IconButton>
                            </Tooltip>
                            <Menu
                                id="long-menu"
                                MenuListProps={{
                                    'aria-labelledby': 'long-button',
                                }}
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                PaperProps={{
                                    style: {
                                        maxHeight: 48 * 4.5,
                                        width: '20ch',
                                    },
                                }}
                            >
                                {columns?.slice(0, -1).map((currentColumn, index) => (
                                    <MenuItem
                                        key={currentColumn.label}
                                        selected={currentColumn.isDisplayed}
                                        onClick={() => {
                                            const newCols = [...columns];
                                            newCols[index].isDisplayed =
                                                !newCols[index].isDisplayed;
                                            if (setColumns) setColumns(newCols);
                                        }}
                                    >
                                        {currentColumn.isDisplayed && (
                                            <CheckIcon fontSize="small" />
                                        )}
                                        {currentColumn.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    </TableCell>
                )}
            </TableRow>
        </TableHead>
    );
};
